import { NavLink, useNavigate } from "react-router-dom";
import Header from "../../components/header";
import MyButton from "../../components/myButton";
import { scrollToForm } from "../../utils/scrolls";
import { Constants } from "../../utils/constants";
import "./styles.scss";

type LegalPageType = "about" | "privacy" | "terms";

type LegalPageProps = {
	type: LegalPageType;
};

const pages: Record<
	LegalPageType,
	{
		eyebrow: string;
		title: string;
		updated: string;
		intro: string;
		sections: { title: string; body: string[] }[];
	}
> = {
	about: {
		eyebrow: "عن الخدمة",
		title: "خدمة فردية مستقلة لإعداد وتنظيم طلبات الشكاوى",
		updated: "آخر تحديث: 5 مايو 2026",
		intro: "هذه الصفحة توضح طبيعة الخدمة كما هي: مبادرة فردية خاصة تساعد المستخدم على ترتيب معلوماته وصياغة طلبه بشكل أوضح قبل أن يقرر الخطوة المناسبة بنفسه.",
		sections: [
			{
				title: "هوية مقدم الخدمة",
				body: [
					`مقدم الخدمة هو ${Constants.companyName} بصفة فردية مستقلة، وليست شركة مسجلة أو منصة تجارية مرخصة.`,
					`الموقع/العنوان العام لمقدم الخدمة: ${Constants.operatorAddress}.`,
					"لا نمثل أي وزارة أو جهة حكومية أو محكمة أو منصة شكاوى رسمية داخل سلطنة عمان أو خارجها.",
					"طريقة التواصل المتاحة حالياً هي نموذج الموقع فقط، ويُستخدم للتواصل الأولي حول الطلب وليس لإنشاء حساب رسمي أو فتح ملف لدى جهة حكومية.",
					`للاستفسارات العامة أو طلب حذف البيانات يمكن التواصل عبر البريد الإلكتروني: ${Constants.contactEmail}.`,
				],
			},
			{
				title: "نطاق الخدمة",
				body: [
					"نساعد في تنظيم المعلومات التي يرسلها العميل، تلخيص الوقائع، وترتيب النقاط المهمة بطريقة واضحة.",
					"الخدمة لا تشمل تقديم استشارات قانونية، أو تمثيل قانوني، أو ممارسة مهنة المحاماة أو الاستشارات القانونية، ولا تتضمن تقديم الشكوى نيابة عن العميل إلى أي جهة رسمية.",
					"لا نراجع صحة المستندات الرسمية، ولا نحدد المركز القانوني للعميل، ولا نوصي بإجراء قانوني محدد؛ دورنا يقتصر على التنظيم والصياغة العامة للمعلومات.",
				],
			},
			{
				title: "ما لا نقدمه",
				body: [
					"لا نقدم ضماناً بقبول الشكوى أو الرد عليها أو حل النزاع أو استرداد مبالغ أو تحقيق نتيجة محددة.",
					"لا نقدم خدمات عاجلة أو طوارئ، ولا ينبغي استخدام الموقع للطلبات التي تحتاج مهلة رسمية قريبة أو إجراء قانوني فوري.",
					"لا نطلب من المستخدم مشاركة كلمات مرور، رموز تحقق، بيانات دفع، أو صلاحيات دخول إلى أي حساب حكومي أو خاص.",
				],
			},
			{
				title: "الترخيص",
				body: [
					"هذه خدمة مساعدة إدارية/تنظيمية مستقلة وليست مكتب محاماة أو شركة استشارات قانونية أو بوابة تجارة إلكترونية لمعالجة الشكاوى.",
					"لذلك لا ندعي امتلاك ترخيص MJLA، أو ترخيص ممارسة الاستشارات القانونية، أو ترخيص منصة إلكترونية حكومية/تجارية لمعالجة الشكاوى.",
					"إذا تبيّن أن طلب العميل يحتاج خدمة مرخصة أو تمثيلاً قانونياً، فيجب عليه التواصل مع محامٍ أو مستشار مرخص أو الجهة الرسمية المختصة مباشرة.",
				],
			},
			{
				title: "الرسوم والدفع",
				body: [
					"الموقع لا يجمع مدفوعات ولا يطلب بيانات بطاقات أو تحويلات داخل النموذج.",
					"أي تكلفة أو ترتيب مدفوع، إن وُجد لاحقاً، يجب توضيحه للعميل قبل المتابعة، وللعميل حرية عدم الاستمرار.",
				],
			},
			{
				title: "ملاحظة للمراجعة",
				body: [
					"English review note: this is an independent individual assistance service for organizing complaint information. It is not a legal practice, law office, government portal, e-commerce complaint platform, or official complaint submission service.",
					"The requested licenses do not apply to the limited service described on this website because no legal advice, legal representation, government filing, official complaint handling platform, or online payment/transaction service is offered through the site.",
				],
			},
		],
	},
	privacy: {
		eyebrow: "سياسة الخصوصية",
		title: "كيف نتعامل مع بيانات النموذج",
		updated: "آخر تحديث: 5 مايو 2026",
		intro: "نطلب فقط البيانات اللازمة للتواصل معك ومراجعة موضوع الطلب. لا نطلب كلمات مرور أو بيانات دفع أو حسابات حكومية.",
		sections: [
			{
				title: "البيانات التي نجمعها",
				body: [
					"الاسم الكامل، البريد الإلكتروني، رقم الهاتف، وتفاصيل الطلب التي تكتبها في النموذج.",
					"قد نُنشئ رقم طلب داخلياً لتسهيل المتابعة، وقد يستخدم الموقع تخزيناً محلياً محدوداً في المتصفح لمنع إرسال الطلب نفسه عدة مرات خلال فترة قصيرة.",
				],
			},
			{
				title: "سبب استخدام البيانات",
				body: [
					"نستخدم البيانات للتواصل معك، فهم موضوع الطلب، ومساعدتك في ترتيب المعلومات التي قدمتها.",
					"لا نستخدم البيانات لتقديم الشكوى رسمياً بالنيابة عنك، ولا ندخل إلى حساباتك أو حسابات الجهات الحكومية.",
				],
			},
			{
				title: "المشاركة والحماية",
				body: [
					"لا نبيع بياناتك ولا نشاركها لأغراض إعلانية خارجية.",
					"قد تُرسل بيانات النموذج عبر مزود خدمة النماذج المستخدم في الموقع لغرض استلام الطلب فقط.",
					"يستخدم الموقع أدوات قياس وتحويلات إعلانية مثل Google tag لفهم أداء الإعلان وقياس إرسال النموذج، وقد تستخدم هذه الأدوات ملفات تعريف ارتباط أو معرفات تقنية وفق إعدادات المتصفح وسياسات Google.",
					"يرجى عدم إرسال كلمات مرور، رموز تحقق، بيانات بطاقات، أو مستندات حساسة غير ضرورية عبر النموذج.",
				],
			},
			{
				title: "الاحتفاظ بالبيانات",
				body: [
					"نحتفظ بالبيانات للمدة اللازمة للتواصل معك ومراجعة الطلب، ثم نحذفها أو نقلل الاحتفاظ بها عندما لا تعود مطلوبة للغرض الذي أُرسلت من أجله.",
					"قد نحتفظ بسجل محدود عند الحاجة لمنع إساءة الاستخدام أو توثيق طلب حذف أو تصحيح.",
				],
			},
			{
				title: "اختياراتك",
				body: [
					"يمكنك عدم استخدام النموذج إذا كنت لا ترغب في إرسال بياناتك.",
					"يمكنك ضبط المتصفح للحد من ملفات تعريف الارتباط أو أدوات القياس، مع العلم أن بعض وظائف القياس أو منع التكرار قد تتأثر.",
				],
			},
			{
				title: "التواصل والحذف",
				body: [
					"يمكنك طلب تصحيح أو حذف بياناتك من خلال إرسال طلب جديد في النموذج وذكر رغبتك بوضوح في خانة التفاصيل.",
					`يمكنك أيضاً التواصل عبر البريد الإلكتروني: ${Constants.contactEmail}.`,
				],
			},
		],
	},
	terms: {
		eyebrow: "شروط الاستخدام",
		title: "شروط واضحة لاستخدام الخدمة",
		updated: "آخر تحديث: 5 مايو 2026",
		intro: "باستخدام هذا الموقع، فإنك تقر بأنك تفهم أن الخدمة مستقلة ومحدودة في المساعدة على التنظيم والصياغة العامة فقط.",
		sections: [
			{
				title: "طبيعة الخدمة",
				body: [
					"الخدمة تساعدك في ترتيب معلومات الشكوى وصياغتها بشكل أوضح بناءً على المعلومات التي تقدمها.",
					"لا نقدم استشارات قانونية، ولا نمثل العملاء أمام الجهات الرسمية، ولا نضمن قبول أي شكوى أو الحصول على نتيجة محددة.",
					"أي نص أو مسودة يتم إعدادها بناءً على معلوماتك هي مساعدة تنظيمية عامة، ويجب عليك مراجعتها بنفسك قبل استخدامها أو إرسالها لأي جهة.",
				],
			},
			{
				title: "مسؤولية المستخدم",
				body: [
					"أنت مسؤول عن صحة المعلومات التي ترسلها وعن قرارك بشأن تقديم أي شكوى إلى الجهة المختصة.",
					"إذا كان موضوعك يحتاج رأياً قانونياً أو تمثيلاً رسمياً، يجب التواصل مع محامٍ أو مستشار مرخص أو الجهة الرسمية المختصة.",
				],
			},
			{
				title: "عدم الانتماء الرسمي",
				body: [
					"الموقع ليس تابعاً لأي جهة حكومية أو وزارة أو محكمة أو منصة رسمية.",
					"أي أسماء لجهات عامة أو منصات شكاوى، إن وردت، تكون لأغراض وصفية فقط ولا تعني وجود اعتماد أو شراكة أو ترخيص.",
					"لا يجوز فهم الخدمة على أنها بديل عن القنوات الرسمية أو عن التواصل المباشر مع الجهة المختصة.",
				],
			},
			{
				title: "الرسوم وطريقة الدفع",
				body: [
					"لا يتم تحصيل أي رسوم أو مدفوعات من خلال نموذج الموقع، ولا يُطلب من المستخدم إدخال بيانات بطاقة أو تحويل بنكي داخل الموقع.",
					"إذا عُرضت أي خدمة مدفوعة لاحقاً خارج الموقع، فيجب توضيح السعر وطريقة الدفع ونطاق الخدمة قبل موافقة المستخدم.",
				],
			},
			{
				title: "الاستخدام غير المناسب",
				body: [
					"يجب عدم إرسال معلومات كاذبة، أو مستندات لا تخصك، أو بيانات حساسة غير ضرورية.",
					"يجب عدم استخدام الموقع لمحاولة تجاوز إجراءات رسمية، أو انتحال صفة جهة أخرى، أو طلب خدمة تتطلب ترخيصاً لا نوفره.",
				],
			},
			{
				title: "حدود المسؤولية",
				body: ["نبذل جهداً لمراجعة المعلومات وتنظيمها بوضوح، لكن لا نضمن المواعيد، أو قرارات الجهات الأخرى، أو النتائج النهائية لأي إجراء يقوم به المستخدم."],
			},
			{
				title: "التواصل",
				body: [
					`العنوان العام لمقدم الخدمة: ${Constants.operatorAddress}.`,
					`للاستفسارات العامة حول طبيعة الخدمة أو شروطها يمكن التواصل عبر البريد الإلكتروني: ${Constants.contactEmail}.`,
				],
			},
		],
	},
};

export default function LegalPage({ type }: LegalPageProps) {
	const page = pages[type];
	const navigate = useNavigate();

	return (
		<main className="legal-page" dir="rtl">
			<div className="legal-header-wrap">
				<Header />
			</div>
			<section className="legal-hero">
				<div className="legal-hero-content">
					<NavLink className="legal-back-link" to="/">
						العودة للرئيسية
					</NavLink>
					<p className="legal-eyebrow">{page.eyebrow}</p>
					<h1>{page.title}</h1>
					<p className="legal-intro">{page.intro}</p>
					<p className="legal-updated">{page.updated}</p>
				</div>
			</section>

			<section className="legal-body">
				{page.sections.map((section) => (
					<article className="legal-section" key={section.title}>
						<h2>{section.title}</h2>
						{section.body.map((paragraph) => (
							<p key={paragraph}>{paragraph}</p>
						))}
					</article>
				))}
				<div className="legal-actions">
					<MyButton onClick={() => scrollToForm(navigate, "#form-card")}>الانتقال إلى نموذج التواصل</MyButton>
				</div>
			</section>
		</main>
	);
}
